import type { FolderRecord } from '../../data-binding/models/FolderRecord';
import {
  OrgAllocationService,
  type AllocationLoadStatus,
  type OrgAllocationAssignment,
} from './OrgAllocationService';

export type OrgPersonWorkContextEmptyReason =
  | 'no_person'
  | 'no_relationships'
  | 'no_identity_match'
  | 'no_active_commitments';

export type OrgPersonWorkContextMatchKind =
  | 'personId'
  | 'employeeNumber'
  | 'email'
  | 'nodeId'
  | 'sourceExternalId'
  | 'name';

export interface OrgPersonWorkContextNode {
  readonly id: string;
  readonly name?: string;
  readonly attrs?: Record<string, unknown> | null;
  readonly props?: Record<string, unknown> | null;
}

export interface OrgPersonWorkContextIdentityCandidate {
  readonly kind: OrgPersonWorkContextMatchKind;
  readonly value: string;
  readonly token: string;
  readonly sourceScopedToken: string;
  readonly strong: boolean;
}

export interface OrgPersonWorkContextMatch {
  readonly kind: OrgPersonWorkContextMatchKind;
  readonly value: string;
  readonly resolvedBy: 'strong_identity' | 'weak_label';
}

export interface OrgPersonWorkContextAssignment extends OrgAllocationAssignment {
  readonly relationshipType: string | null;
  readonly matchedBy: OrgPersonWorkContextMatch;
  readonly sourceLabel: string | null;
  readonly sourceSystem: string | null;
  readonly resolutionStatus: 'resolved_same_as' | 'direct' | 'unresolved';
  readonly resolution: Record<string, unknown> | null;
}

export interface OrgPersonWorkContext {
  readonly personNodeId: string | null;
  readonly personLabel: string | null;
  readonly asOf: string;
  readonly identityCandidates: readonly OrgPersonWorkContextIdentityCandidate[];
  readonly activeAssignments: readonly OrgPersonWorkContextAssignment[];
  readonly inactiveAssignments: readonly OrgPersonWorkContextAssignment[];
  readonly totalAllocation: number;
  readonly assignmentCount: number;
  readonly inactiveAssignmentCount: number;
  readonly hasUnspecifiedAllocation: boolean;
  readonly status: AllocationLoadStatus;
  readonly emptyReason: OrgPersonWorkContextEmptyReason | null;
  readonly possibleWeakLabelMatchCount: number;
}

export interface OrgPersonWorkContextOptions {
  readonly asOf?: Date | string;
  readonly capacityTarget?: number;
}

const STRONG_IDENTITY_KEYS = [
  'personId',
  'employeeNumber',
  'employeeId',
  'workerId',
  'email',
  'mail',
  'userPrincipalName',
  'upn',
  'sourceExternalId',
] as const;

const NAME_KEYS = [
  'name',
  'displayName',
  'preferredName',
  'firstName',
  'lastName',
] as const;

const SOURCE_ROW_SUBJECT_HINTS = [
  'actor',
  'assignee',
  'assignedto',
  'person',
  'employee',
  'worker',
  'member',
  'owner',
  'responsible',
  'user',
] as const;

const SOURCE_ROW_STRONG_IDENTITY_HINTS = [
  'email',
  'mail',
  'upn',
  'userprincipalname',
  'personid',
  'employeeid',
  'employeenumber',
  'workerid',
  'userid',
] as const;

const DEFAULT_CAPACITY_TARGET = 1;

/**
 * Selected-person read model over Atlas workload relationship facts.
 *
 * This service deliberately projects folder relationship records onto a person
 * node without writing anything back to the canvas node. Label-only matches are
 * exposed as weak candidates, but never treated as resolved workload.
 */
export class OrgPersonWorkContextService {
  constructor(private readonly allocationService = new OrgAllocationService()) {}

  buildContext(
    personNode: OrgPersonWorkContextNode | null | undefined,
    relationshipRecords: readonly FolderRecord[],
    options: OrgPersonWorkContextOptions = {},
  ): OrgPersonWorkContext {
    const asOf = toDate(options.asOf) ?? new Date();
    const base = emptyContext(personNode, asOf, identityCandidatesForNode(personNode));
    if (!personNode) return { ...base, emptyReason: 'no_person' };

    const assignments = relationshipRecords
      .map((record) => assignmentFromRecord(this.allocationService, record))
      .filter((entry): entry is AssignmentRecordPair => entry !== null);
    if (assignments.length === 0) {
      return { ...base, emptyReason: 'no_relationships' };
    }

    const candidates = base.identityCandidates;
    const matched = assignments
      .map(({ record, assignment }) => {
        const match = matchAssignmentToPerson(assignment, record, candidates);
        return match
          ? enrichAssignment(record, assignment, match)
          : null;
      })
      .filter((assignment): assignment is OrgPersonWorkContextAssignment => assignment !== null);

    if (matched.length === 0) {
      return {
        ...base,
        emptyReason: 'no_identity_match',
        possibleWeakLabelMatchCount: weakLabelMatchCount(assignments, candidates),
      };
    }

    const activeAssignments = matched
      .filter((assignment) => isActiveAt(assignment, asOf))
      .sort(compareAssignments);
    const inactiveAssignments = matched
      .filter((assignment) => !isActiveAt(assignment, asOf))
      .sort(compareAssignments);

    const totalAllocation = sumKnownAllocations(activeAssignments);
    const hasUnspecifiedAllocation = activeAssignments.some(
      (assignment) => assignment.hasUnspecifiedAllocation,
    );

    return {
      ...base,
      activeAssignments,
      inactiveAssignments,
      totalAllocation,
      assignmentCount: activeAssignments.length,
      inactiveAssignmentCount: inactiveAssignments.length,
      hasUnspecifiedAllocation,
      status: loadStatus(
        totalAllocation,
        hasUnspecifiedAllocation,
        clampCapacityTarget(options.capacityTarget),
      ),
      emptyReason: activeAssignments.length > 0 ? null : 'no_active_commitments',
    };
  }
}

interface AssignmentRecordPair {
  readonly record: FolderRecord;
  readonly assignment: OrgAllocationAssignment;
}

function assignmentFromRecord(
  allocationService: OrgAllocationService,
  record: FolderRecord,
): AssignmentRecordPair | null {
  const assignment = allocationService.relationshipRecordToAssignment(record);
  return assignment ? { record, assignment } : null;
}

function emptyContext(
  personNode: OrgPersonWorkContextNode | null | undefined,
  asOf: Date,
  identityCandidates: readonly OrgPersonWorkContextIdentityCandidate[],
): OrgPersonWorkContext {
  return {
    personNodeId: personNode?.id ?? null,
    personLabel: personLabel(personNode),
    asOf: asOf.toISOString(),
    identityCandidates,
    activeAssignments: [],
    inactiveAssignments: [],
    totalAllocation: 0,
    assignmentCount: 0,
    inactiveAssignmentCount: 0,
    hasUnspecifiedAllocation: false,
    status: 'unknown',
    emptyReason: null,
    possibleWeakLabelMatchCount: 0,
  };
}

export function identityCandidatesForNode(
  personNode: OrgPersonWorkContextNode | null | undefined,
): OrgPersonWorkContextIdentityCandidate[] {
  if (!personNode) return [];
  const attrs = isRecord(personNode.attrs) ? personNode.attrs : {};
  const props = isRecord(personNode.props) ? personNode.props : {};
  const out: OrgPersonWorkContextIdentityCandidate[] = [];

  addCandidate(out, 'nodeId', personNode.id, true);
  for (const key of STRONG_IDENTITY_KEYS) {
    addCandidate(out, candidateKindForKey(key), props[key] ?? attrs[key], true);
  }

  const fullName = personLabel(personNode);
  addCandidate(out, 'name', fullName, false);
  for (const key of NAME_KEYS) {
    addCandidate(out, 'name', props[key] ?? attrs[key], false);
  }

  return uniqueCandidates(out);
}

function addCandidate(
  out: OrgPersonWorkContextIdentityCandidate[],
  kind: OrgPersonWorkContextMatchKind,
  value: unknown,
  strong: boolean,
): void {
  const raw = stringValue(value);
  if (!raw) return;
  const token = normalizeToken(raw);
  if (!token) return;
  out.push({
    kind,
    value: raw,
    token,
    sourceScopedToken: safeSegment(raw),
    strong,
  });
}

function candidateKindForKey(key: typeof STRONG_IDENTITY_KEYS[number]): OrgPersonWorkContextMatchKind {
  if (key === 'email' || key === 'mail' || key === 'userPrincipalName' || key === 'upn') {
    return 'email';
  }
  if (key === 'employeeNumber' || key === 'employeeId' || key === 'workerId') {
    return 'employeeNumber';
  }
  return key;
}

function uniqueCandidates(
  candidates: readonly OrgPersonWorkContextIdentityCandidate[],
): OrgPersonWorkContextIdentityCandidate[] {
  const seen = new Set<string>();
  const out: OrgPersonWorkContextIdentityCandidate[] = [];
  for (const candidate of candidates) {
    const key = `${candidate.kind}:${candidate.token}:${candidate.strong ? 'strong' : 'weak'}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(candidate);
  }
  return out;
}

function matchAssignmentToPerson(
  assignment: OrgAllocationAssignment,
  record: FolderRecord,
  candidates: readonly OrgPersonWorkContextIdentityCandidate[],
): OrgPersonWorkContextMatch | null {
  const strongCandidates = candidates.filter((candidate) => candidate.strong);
  const endpointValues = relationshipSubjectValues(assignment, record);
  for (const candidate of strongCandidates) {
    if (endpointValues.some((value) => strongValueMatchesCandidate(value, candidate))) {
      return {
        kind: candidate.kind,
        value: candidate.value,
        resolvedBy: 'strong_identity',
      };
    }
  }
  return null;
}

function weakLabelMatchCount(
  assignments: readonly AssignmentRecordPair[],
  candidates: readonly OrgPersonWorkContextIdentityCandidate[],
): number {
  const weakNames = new Set(candidates
    .filter((candidate) => !candidate.strong && candidate.kind === 'name')
    .map((candidate) => candidate.token));
  if (weakNames.size === 0) return 0;
  return assignments.filter(({ assignment }) =>
    weakNames.has(normalizeToken(assignment.personLabel)) ||
    weakNames.has(normalizeToken(assignment.actorLabel))
  ).length;
}

function relationshipSubjectValues(
  assignment: OrgAllocationAssignment,
  record: FolderRecord,
): string[] {
  const props = record.universalProps;
  const resolution = isRecord(props.resolution) ? props.resolution : {};
  const subjectResolution = isRecord(resolution.subject) ? resolution.subject : {};
  return uniqueStrings([
    assignment.personExternalId,
    assignment.actorExternalId,
    assignment.personLabel,
    assignment.actorLabel,
    stringValue(props.subjectExternalId),
    stringValue(props.actorExternalId),
    stringValue(props.subjectLabel),
    stringValue(props.actorLabel),
    stringValue(subjectResolution.originalExternalId),
    stringValue(subjectResolution.resolvedExternalId),
    stringValue(subjectResolution.originalLabel),
    stringValue(subjectResolution.resolvedLabel),
    ...sourceRowSubjectIdentityValues(props.source),
  ]);
}

function sourceRowSubjectIdentityValues(source: unknown): string[] {
  if (!isRecord(source)) return [];
  const rowSnapshot = isRecord(source.rowSnapshot) ? source.rowSnapshot : null;
  if (!rowSnapshot) return [];
  return Object.entries(rowSnapshot)
    .filter(([key]) => isSourceRowSubjectIdentityHeader(key))
    .map(([, value]) => stringValue(value))
    .filter((value): value is string => !!value);
}

function isSourceRowSubjectIdentityHeader(header: string): boolean {
  const normalized = header.toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (!normalized) return false;
  const hasIdentityHint = SOURCE_ROW_STRONG_IDENTITY_HINTS.some((hint) =>
    normalized.includes(hint));
  if (!hasIdentityHint) return false;
  if ((SOURCE_ROW_STRONG_IDENTITY_HINTS as readonly string[]).includes(normalized)) return true;
  return SOURCE_ROW_SUBJECT_HINTS.some((hint) => normalized.startsWith(hint));
}

function strongValueMatchesCandidate(
  rawValue: string,
  candidate: OrgPersonWorkContextIdentityCandidate,
): boolean {
  const valueToken = normalizeToken(rawValue);
  if (!valueToken) return false;
  if (valueToken === candidate.token) return true;

  const safeValue = safeSegment(rawValue);
  if (safeValue === candidate.sourceScopedToken) return true;

  // Source-scoped external IDs include safe identity segments plus a hash.
  // Example: spreadsheet:capacity-commitment:actor:sarah-example-com-abc123
  return safeValue.includes(candidate.sourceScopedToken) ||
    valueToken.includes(candidate.token);
}

function enrichAssignment(
  record: FolderRecord,
  assignment: OrgAllocationAssignment,
  matchedBy: OrgPersonWorkContextMatch,
): OrgPersonWorkContextAssignment {
  const props = record.universalProps;
  const resolution = isRecord(props.resolution) ? props.resolution : null;
  const subjectResolution = resolution && isRecord(resolution.subject)
    ? resolution.subject
    : null;
  return {
    ...assignment,
    relationshipType: stringValue(props.relationshipType) ?? stringValue(props.predicate),
    matchedBy,
    sourceLabel: sourceLabel(props.source),
    sourceSystem: sourceSystem(props.source),
    resolutionStatus: subjectResolution ? 'resolved_same_as' : 'direct',
    resolution,
  };
}

function personLabel(personNode: OrgPersonWorkContextNode | null | undefined): string | null {
  if (!personNode) return null;
  const attrs = isRecord(personNode.attrs) ? personNode.attrs : {};
  const props = isRecord(personNode.props) ? personNode.props : {};
  const firstLast = [
    stringValue(props.firstName) ?? stringValue(attrs.firstName),
    stringValue(props.lastName) ?? stringValue(attrs.lastName),
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
  return stringValue(props.name) ??
    stringValue(attrs.name) ??
    stringValue(personNode.name) ??
    stringValue(firstLast);
}

function sourceLabel(value: unknown): string | null {
  if (!isRecord(value)) return null;
  return stringValue(value.label) ??
    stringValue(value.connectorId) ??
    stringValue(value.mappingRef) ??
    null;
}

function sourceSystem(value: unknown): string | null {
  if (!isRecord(value)) return null;
  return stringValue(value.sourceSystem) ??
    stringValue(value.connectorId) ??
    null;
}

function compareAssignments(
  left: OrgPersonWorkContextAssignment,
  right: OrgPersonWorkContextAssignment,
): number {
  return left.targetKind.localeCompare(right.targetKind) ||
    left.targetLabel.localeCompare(right.targetLabel);
}

function loadStatus(
  totalAllocation: number,
  hasUnspecifiedAllocation: boolean,
  capacityTarget: number,
): AllocationLoadStatus {
  if (hasUnspecifiedAllocation && totalAllocation === 0) return 'unknown';
  if (totalAllocation > capacityTarget + 0.0001) return 'over_allocated';
  if (Math.abs(totalAllocation - capacityTarget) <= 0.0001) return 'fully_allocated';
  return 'under_allocated';
}

function sumKnownAllocations(assignments: readonly OrgAllocationAssignment[]): number {
  return assignments.reduce((sum, assignment) => sum + (assignment.allocation ?? 0), 0);
}

function isActiveAt(assignment: OrgAllocationAssignment, asOf: Date): boolean {
  const start = toDate(assignment.validFrom);
  const end = toDate(assignment.validTo);
  if (start && start.getTime() > asOf.getTime()) return false;
  if (end && end.getTime() < asOf.getTime()) return false;
  return true;
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function clampCapacityTarget(value: number | undefined): number {
  if (!value || !Number.isFinite(value) || value <= 0) return DEFAULT_CAPACITY_TARGET;
  return Math.max(0.01, Math.min(10, value));
}

function safeSegment(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '');
}

function normalizeToken(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function stringValue(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const raw = String(value).trim();
  return raw ? raw : null;
}

function uniqueStrings(values: readonly (string | null | undefined)[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const raw = value?.trim();
    if (!raw) continue;
    const key = normalizeToken(raw);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(raw);
  }
  return out;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
